/**
 * Program-level pricing utilities.
 *
 * Programs store fees in cents (integer). Division-level fees override the
 * program default. Early-bird discount applies program-wide.
 */

import { calcProcessingFee } from './pricing';

/**
 * Calculate the fee for a given program + division.
 *
 * @param {Object} program       - Firestore program document data (or null)
 * @param {string} divisionId    - id of the selected division
 * @returns {{
 *   subtotalAmount: number,    // cents before processing fee
 *   processingFeeMode: string, // 'absorb' | 'pass_to_parent'
 *   processingFee: number,     // cents added when pass_to_parent
 *   finalAmount: number,       // cents actually charged (subtotal + fee)
 *   baseFee: number,           // non-discounted fee in cents
 *   isEarlyBird: boolean,
 *   label: string,             // "Early Bird" | "Standard" | "Free"
 *   divisionName: string,
 * }}
 */
export function calculateProgramPrice(program, divisionId) {
  if (!program) {
    return {
      subtotalAmount: 0,
      processingFeeMode: 'absorb',
      processingFee: 0,
      finalAmount: 0,
      baseFee: 0,
      isEarlyBird: false,
      label: 'Free',
      divisionName: '',
    };
  }

  const division = (program.divisions ?? []).find((d) => d.id === divisionId);

  const now = Date.now();
  const earlyBirdDeadlineMs = program.earlyBirdDeadline?.toMillis?.() ?? 0;
  const isEarlyBird =
    program.earlyBirdFee > 0 &&
    earlyBirdDeadlineMs > 0 &&
    now < earlyBirdDeadlineMs;

  // Division-specific fee takes priority; fall back to program default
  const baseFee = division?.fee ?? program.defaultFee ?? 0;

  // Early bird discount applies program-wide (not per-division for MVP)
  const subtotalAmount = isEarlyBird ? (program.earlyBirdFee ?? baseFee) : baseFee;

  const processingFeeMode = program.processingFeeMode === 'pass_to_parent'
    ? 'pass_to_parent'
    : 'absorb';
  const processingFee = calcProcessingFee(subtotalAmount, processingFeeMode);
  const finalAmount   = subtotalAmount + processingFee;

  return {
    subtotalAmount,
    processingFeeMode,
    processingFee,
    finalAmount,
    baseFee,
    isEarlyBird,
    label: finalAmount === 0 ? 'Free' : isEarlyBird ? 'Early Bird' : 'Standard',
    divisionName: division?.name ?? '',
  };
}

/**
 * Format cents as a dollar string.
 * e.g. 9900 → "$99.00",  0 → "Free"
 */
export function formatCents(cents) {
  if (!cents && cents !== 0) return '—';
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(2)}`;
}
